import { FC, useState } from 'react';
import { Control, Controller, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useEligibleChildren, usePendingOnboardings } from '../../../../common/apiHooks';
import { ChildOnboardingFormData } from '../types';
import { pendingChildOnboardings } from '../onboardingFlows';
import { isValidEstonianPersonalCode } from './personalCode';
import { PII_CLASS } from '../../../../tracking/piiMarkup';

type ChildIdentityStepProps = {
  control: Control<ChildOnboardingFormData>;
};

export const ChildIdentityStep: FC<ChildIdentityStepProps> = ({ control }) => {
  const intl = useIntl();
  const { data: eligibleChildren = [], isLoading } = useEligibleChildren();
  const { data: pendingOnboardings = [] } = usePendingOnboardings();
  const joinableChildCodes = new Set(
    pendingChildOnboardings(pendingOnboardings).map(({ code }) => code),
  );
  const [manualEntry, setManualEntry] = useState(false);
  const childPersonalCode = useWatch({ control, name: 'childPersonalCode' });
  const enteredCodeIsSelectable =
    !childPersonalCode ||
    eligibleChildren.some(({ personalCode }) => personalCode === childPersonalCode);
  const showDropdown = !manualEntry && eligibleChildren.length > 0 && enteredCodeIsSelectable;

  return (
    <section className="d-flex flex-column gap-4" key="child-identity">
      <div className="d-flex flex-column gap-1">
        <h2 className="m-0">
          <FormattedMessage id="flows.savingsFundChildOnboarding.identityStep.title" />
        </h2>
        <p className="m-0">
          <FormattedMessage
            id={
              showDropdown
                ? 'flows.savingsFundChildOnboarding.identityStep.selectDescription'
                : 'flows.savingsFundChildOnboarding.identityStep.description'
            }
          />
        </p>
      </div>
      <div className="section-content d-flex flex-column gap-4">
        <Controller
          control={control}
          name="childPersonalCode"
          rules={{
            required: {
              value: true,
              message: intl.formatMessage({
                id: 'flows.savingsFundChildOnboarding.identityStep.required',
              }),
            },
            validate: (value) =>
              isValidEstonianPersonalCode(value) ||
              intl.formatMessage({
                id: 'flows.savingsFundChildOnboarding.identityStep.invalid',
              }),
          }}
          render={({ field, fieldState: { error } }) => (
            <div className="d-flex flex-column gap-2">
              <div>
                <label htmlFor={field.name} className="form-label">
                  <FormattedMessage id="flows.savingsFundChildOnboarding.identityStep.label" />
                </label>
                {showDropdown ? (
                  <select
                    {...field}
                    id={field.name}
                    className={`form-select form-select-lg ${PII_CLASS}`}
                  >
                    <option value="">
                      {intl.formatMessage({
                        id: 'flows.savingsFundChildOnboarding.identityStep.selectPlaceholder',
                      })}
                    </option>
                    {eligibleChildren.map(
                      ({ personalCode, firstName, lastName, hasBeenOnboarded }) => {
                        const name = [firstName, lastName].filter(Boolean).join(' ');
                        const label = name ? `${name} (${personalCode})` : personalCode;
                        const alreadyOpened =
                          Boolean(hasBeenOnboarded) && !joinableChildCodes.has(personalCode);
                        return (
                          <option key={personalCode} value={personalCode} disabled={alreadyOpened}>
                            {alreadyOpened
                              ? `${label} · ${intl.formatMessage({
                                  id: 'flows.savingsFundChildOnboarding.identityStep.accountOpened',
                                })}`
                              : label}
                          </option>
                        );
                      },
                    )}
                  </select>
                ) : (
                  <input
                    {...field}
                    id={field.name}
                    // A focused input swapped for the select would silently steal focus.
                    disabled={isLoading && !manualEntry}
                    inputMode="numeric"
                    autoComplete="off"
                    className="form-control form-control-lg"
                    placeholder={intl.formatMessage({
                      id: 'flows.savingsFundChildOnboarding.identityStep.placeholder',
                    })}
                  />
                )}
                {error && error.message ? (
                  <p className="m-0 mt-1 text-danger fs-base" role="alert">
                    {error.message}
                  </p>
                ) : null}
              </div>
              {showDropdown || (isLoading && !manualEntry) ? (
                <button
                  type="button"
                  className="btn btn-link p-0 align-self-start"
                  onClick={() => setManualEntry(true)}
                >
                  <FormattedMessage id="flows.savingsFundChildOnboarding.identityStep.enterManually" />
                </button>
              ) : null}
            </div>
          )}
        />
      </div>
    </section>
  );
};
